import { AxiosError, type AxiosResponse } from 'axios'

import {
  type DataResponse,
  type User,
  type UpdateResponse,
  type ErrorResponse,
  type LearningKeyword,
  type SearchedExam,
  type SurveySubmitRequest,
  type SuggestBooks,
  type StatsOption,
  type StatsData,
  type UserSurvey,
  type Dday,
} from './interfaces/sconnStudyData'
import type MockSconnStudyDataClient from './mockSconnStudyDataClient'
import type SconnStudyDataClient from './sconnStudyDataClient'

export default class SconnStudyData {
  readonly #apiClient: MockSconnStudyDataClient | SconnStudyDataClient

  constructor(apiClient: any) {
    this.#apiClient = apiClient
  }

  setAccessToken(accessToken: string) {
    this.#apiClient.setAccessToken(accessToken)
  }

  setExpires(expires: number) {
    this.#apiClient.expires = expires
  }

  /**
   * 학습키워드 트리 조회.
   * V2에서 /list/cert, /list/uc, /list/high, /list/middle 은 모두 동일한
   * 학습키워드 트리를 반환하므로 /list/usertype 하나로 통일됨.
   */
  async getLearningKeywords() {
    return await this.#apiClient
      .getLearningKeywords()
      .then(handleListDataResponse<LearningKeyword>)
      .catch(handleErrorResolver)
  }

  async getUserInformation() {
    return await this.#apiClient
      .getUserInformation()
      .then(handleDataResponse<User>)
      .catch(handleErrorResolver)
  }

  async getUserSurvey() {
    return await this.#apiClient
      .getUserSurvey()
      .then(handleDataResponse<UserSurvey>)
      .catch(handleErrorResolver)
  }

  async getUserDday() {
    return await this.#apiClient
      .getUserDday()
      .then(handleDataResponse<Dday>)
      .catch(handleErrorResolver)
  }

  async postUserDday(dday: Dday) {
    return await this.#apiClient
      .postUserDday(dday)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async putGoalDaily(seconds: number, offset: number) {
    return await this.#apiClient
      .putGoalDaily(seconds, offset)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async postGoalDaily(seconds: number, offset: number) {
    return await this.#apiClient
      .postGoalDaily(seconds, offset)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async getUserSuggests() {
    return await this.#apiClient
      .getUserSuggests()
      .then(handleDataResponse<SuggestBooks>)
      .catch(handleErrorResolver)
  }

  async getUserStatistics(option: StatsOption) {
    return await this.#apiClient
      .getUserStatistics(option)
      .then(handleDataResponse<StatsData>)
      .catch(handleErrorResolver)
  }

  async postNickname(nickName: string) {
    return await this.#apiClient
      .postNickname(nickName)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async validateNickname(nickName: string) {
    return await this.#apiClient
      .validateNickname(nickName)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async postConsentSurvey(allow: boolean) {
    return await this.#apiClient
      .postConsentAllow(allow)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  async postSurvey(survey: SurveySubmitRequest) {
    return await this.#apiClient
      .postSurvey(survey)
      .then((res: AxiosResponse<UpdateResponse>) => res.data)
      .catch(handleErrorResolver)
  }

  /**
   * 시험 이름 검색 (POST /search/cert).
   * 전체 목록(/list/cert)을 받아 프론트에서 필터링하던 방식이 서버 검색으로 대체됨.
   */
  async searchExams(searchWord: string) {
    return await this.#apiClient
      .searchExams(searchWord)
      .then(handleListDataResponse<SearchedExam>)
      .catch(handleErrorResolver)
  }
}

function handleListDataResponse<T>(res: AxiosResponse<DataResponse<T[]>>) {
  const { data: lists } = res.data
  return lists === null || lists?.length === 0 ? [] : lists
}

function handleDataResponse<T>(res: AxiosResponse<DataResponse<T>>) {
  return res.data.data
}

function handleErrorResolver(error: AxiosError<ErrorResponse>): never {
  if (error.response?.data) {
    throw new AxiosError(error.response?.data.reason)
  }

  throw new AxiosError(error.message)
}
